/**
 * Phase F11 FE-3 — proctor artifact gallery.
 *
 * Renders the per-attempt gallery of :class:`ReviewProctorArtifact`
 * thumbnails grouped by ``kind`` (id_document, webcam_still,
 * screen_capture). Used inside :class:`ProctorReviewPanel`.
 *
 * Lazy-load posture: every thumbnail starts in a "click to load"
 * state. On first click we mint a signed GET URL via
 * :func:`useMintProctorDownloadUrl`, render the bytes inline (with
 * an open-in-new-tab affordance), and cache the URL until the
 * component re-mounts. We don't speculatively prefetch all URLs on
 * mount because (a) the mint endpoint is per-row and (b) the URLs
 * are short-lived (10 minutes).
 *
 * Privacy posture: the BE never surfaces ``storage_object_path``;
 * the FE never persists or echoes the ``get_url`` past the
 * component's own React state. A reload re-mints fresh URLs.
 *
 * Accessibility: thumbnails are buttons, not anchors — each click
 * triggers a state-only mutation rather than a page navigation, so
 * `<button>` is the correct semantic. The expanded preview gets a
 * ``role="img"`` + ``aria-label`` derived from the artifact metadata.
 */
import * as React from "react";
import { motion } from "motion/react";
import { AlertTriangle, ExternalLink, IdCard, Loader2, Monitor, Camera } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import type { ReviewProctorArtifact, ReviewProctorArtifactKind } from "@eduviaos/shared";
import { useMintProctorDownloadUrl } from "@/hooks/useAdmissions";

interface ProctorArtifactGalleryProps {
  attemptId: string;
  artifacts: ReviewProctorArtifact[];
}

const _KIND_LABEL: Record<ReviewProctorArtifactKind, string> = {
  id_document: "ID document",
  webcam_still: "Webcam stills",
  screen_capture: "Screen captures",
};

const _KIND_ORDER: ReviewProctorArtifactKind[] = ["id_document", "webcam_still", "screen_capture"];

export function ProctorArtifactGallery({ attemptId, artifacts }: ProctorArtifactGalleryProps) {
  const grouped = React.useMemo(() => {
    const groups: Record<ReviewProctorArtifactKind, ReviewProctorArtifact[]> = {
      id_document: [],
      webcam_still: [],
      screen_capture: [],
    };
    for (const a of artifacts) {
      groups[a.kind].push(a);
    }
    return groups;
  }, [artifacts]);

  if (artifacts.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No proctor artifacts were captured for this attempt.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {_KIND_ORDER.map((kind) => {
        const items = grouped[kind];
        if (items.length === 0) return null;
        return (
          <section key={kind} aria-labelledby={`proctor-kind-${kind}`}>
            <header className="mb-2 flex items-baseline justify-between">
              <h4 id={`proctor-kind-${kind}`} className="text-sm font-semibold">
                {_KIND_LABEL[kind]}
              </h4>
              <span className="text-muted-foreground text-xs">{items.length} captured</span>
            </header>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((a) => (
                <ArtifactTile key={a.id} attemptId={attemptId} artifact={a} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ArtifactTile({
  attemptId,
  artifact,
}: {
  attemptId: string;
  artifact: ReviewProctorArtifact;
}) {
  const mint = useMintProctorDownloadUrl();
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const isCommitted = artifact.status === "committed";

  const onLoad = React.useCallback(async () => {
    if (!isCommitted) return;
    setError(null);
    try {
      const res = await mint.mutateAsync({
        attemptId,
        artifactId: artifact.id,
      });
      setPreviewUrl(res.get_url);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [attemptId, artifact.id, isCommitted, mint]);

  return (
    <motion.figure
      className="border-border bg-background overflow-hidden rounded-md border shadow-sm"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16 }}
    >
      <div className="bg-muted/40 relative flex aspect-[4/3] items-center justify-center">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`${_KIND_LABEL[artifact.kind]} captured at ${new Date(
              artifact.captured_at,
            ).toLocaleString()}`}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <button
            type="button"
            onClick={() => void onLoad()}
            disabled={!isCommitted || mint.isPending}
            className="text-muted-foreground hover:text-foreground flex flex-col items-center gap-2 px-4 text-center text-xs disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={`Load preview for ${_KIND_LABEL[artifact.kind]} captured at ${new Date(
              artifact.captured_at,
            ).toLocaleString()}`}
          >
            {mint.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <KindIcon kind={artifact.kind} />
            )}
            <span>
              {!isCommitted
                ? statusCopy(artifact.status)
                : mint.isPending
                  ? "Loading…"
                  : "Click to preview"}
            </span>
          </button>
        )}
      </div>
      <figcaption className="space-y-1 px-3 py-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground tabular-nums">
            {new Date(artifact.captured_at).toLocaleString(undefined, {
              dateStyle: "short",
              timeStyle: "medium",
            })}
          </span>
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary inline-flex items-center gap-0.5 hover:underline"
              aria-label="Open in new tab"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
              full
            </a>
          )}
        </div>
        {artifact.bytes_size !== null && (
          <p className="text-muted-foreground">
            {formatBytes(artifact.bytes_size)} · {artifact.mime_type}
          </p>
        )}
        {error && (
          <p role="alert" className="text-destructive flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" aria-hidden /> {error}
          </p>
        )}
        {!isCommitted && <p className="text-amber-600">Status: {statusCopy(artifact.status)}</p>}
        {previewUrl && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-full text-xs"
            onClick={() => void onLoad()}
            disabled={mint.isPending}
          >
            Refresh URL
          </Button>
        )}
      </figcaption>
    </motion.figure>
  );
}

function KindIcon({ kind }: { kind: ReviewProctorArtifactKind }) {
  switch (kind) {
    case "id_document":
      return <IdCard className="h-5 w-5" aria-hidden />;
    case "webcam_still":
      return <Camera className="h-5 w-5" aria-hidden />;
    case "screen_capture":
      return <Monitor className="h-5 w-5" aria-hidden />;
  }
}

function statusCopy(status: ReviewProctorArtifact["status"]): string {
  switch (status) {
    case "pending_upload":
      return "Still uploading…";
    case "failed":
      return "Upload failed";
    case "committed":
      return "Committed";
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function errorMessage(err: unknown): string {
  if (err === null || err === undefined) return "Failed to load preview.";
  if (typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Failed to load preview.";
}
